import type { CreditConfig, HeadpatWebConfig } from "./vts-config";
import { loadConfiguredVtsModel } from "./vts-config";

const defaultHeadpatGifs = [
  "/assets/headpats_pichuurisu.gif",
  "/assets/headpats_with_hearts_pichuurisu.gif",
];
const defaultHeadpatCursor = "/assets/headpat_cursor_pichuurisu.png";
const nativeCursorPaddingPx = 60;

let isInitialized = false;

export async function initializeHeadpatUi(): Promise<void> {
  if (isInitialized) {
    return;
  }
  isInitialized = true;

  const { app } = await loadConfiguredVtsModel();
  applyPageConfig(app);
  setupHeadpatEffects(app);
}

function applyPageConfig(config: HeadpatWebConfig): void {
  document.title = config.title;

  const title = document.getElementById("pageTitle");
  if (title) {
    title.textContent = config.title;
  }

  if (config.page?.backgroundImage) {
    document.documentElement.style.setProperty(
      "--app-background-image",
      `url("${config.page.backgroundImage}")`,
    );
  }

  if (config.page?.icon) {
    const icon = document.getElementById("appIcon") as HTMLLinkElement | null;
    icon?.setAttribute("href", config.page.icon);
  }

  renderCredits(config.page?.credits ?? []);
}

function renderCredits(credits: CreditConfig[]): void {
  const creditsRoot = document.getElementById("credits");
  if (!creditsRoot) {
    return;
  }

  creditsRoot.replaceChildren();
  if (credits.length === 0) {
    creditsRoot.hidden = true;
    return;
  }

  creditsRoot.hidden = false;
  creditsRoot.classList.remove("is-expanded");
  const primaryCredits = credits.filter((credit) => !credit.more);
  const moreCredits = credits.filter((credit) => credit.more);

  const list = document.createElement("ul");
  list.id = "creditsList";
  primaryCredits.forEach((credit) => list.append(createCreditItem(credit)));
  creditsRoot.append(list);

  if (moreCredits.length === 0) {
    return;
  }

  const toggle = document.createElement("button");
  toggle.id = "creditsToggle";
  toggle.className = "credits-toggle";
  toggle.type = "button";
  toggle.textContent = "More credits";
  toggle.setAttribute("aria-expanded", "false");
  toggle.setAttribute("aria-controls", "creditsMoreList");
  toggle.addEventListener("click", () => {
    const isExpanded = creditsRoot.classList.toggle("is-expanded");
    toggle.setAttribute("aria-expanded", String(isExpanded));
  });

  const moreList = document.createElement("ul");
  moreList.id = "creditsMoreList";
  moreList.className = "credits-more-list";
  moreCredits.forEach((credit) => moreList.append(createCreditItem(credit)));

  creditsRoot.append(toggle);
  creditsRoot.append(moreList);
}

function createCreditItem(credit: CreditConfig): HTMLLIElement {
  const item = document.createElement("li");

  if (credit.text) {
    item.textContent = credit.text;
    return item;
  }

  item.append(document.createTextNode(credit.before ?? ""));
  if (credit.href && credit.label) {
    const link = document.createElement("a");
    link.href = credit.href;
    link.textContent = credit.label;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    item.append(link);
  } else if (credit.label) {
    item.append(document.createTextNode(credit.label));
  }
  item.append(document.createTextNode(credit.after ?? ""));

  return item;
}

function setupHeadpatEffects(config: HeadpatWebConfig): void {
  const gif = document.getElementById("cursorGif") as HTMLImageElement | null;
  const cursorImage = config.headpat?.cursorImage ?? defaultHeadpatCursor;
  const gifs = config.headpat?.gifs?.length
    ? config.headpat.gifs
    : defaultHeadpatGifs;

  if (!gif) {
    return;
  }

  const counter = createCounter(config);
  let gifIndex = -1;
  let isPressing = false;
  let holdTimeout: number | undefined;
  document.documentElement.classList.add("headpat-cursor-ready");
  setCursorSource(cursorImage, "idle");

  const showCursor = (x: number, y: number): void => {
    gif.style.display = "block";
    document.documentElement.classList.remove("headpat-native-cursor-zone");
    moveCursor(x, y);
  };

  const moveCursor = (x: number, y: number): void => {
    gif.style.left = `${x}px`;
    gif.style.top = `${y}px`;
  };

  const hideCursor = (): void => {
    gif.style.display = "none";
    document.documentElement.dataset.openvtHeadpatActive = "0";
  };

  const useNativeCursor = (): void => {
    hideCursor();
    document.documentElement.classList.add("headpat-native-cursor-zone");
  };

  const returnToIdleCursor = (): void => {
    document.documentElement.dataset.openvtHeadpatActive = "0";
    setCursorSource(cursorImage, "idle");
  };

  function setCursorSource(source: string, mode: "idle" | "patting"): void {
    if (gif.dataset.cursorMode === mode && gif.getAttribute("src") === source) {
      return;
    }

    gif.dataset.cursorMode = mode;
    gif.src = source;
  }

  const endHold = (): void => {
    if (holdTimeout !== undefined) {
      window.clearTimeout(holdTimeout);
      holdTimeout = undefined;
    }
  };

  document.addEventListener(
    "pointermove",
    (event: PointerEvent): void => {
      if (shouldUseNativeCursor(event)) {
        useNativeCursor();
        return;
      }

      showCursor(event.clientX, event.clientY);
    },
    { passive: true },
  );

  document.addEventListener(
    "pointerenter",
    (event: PointerEvent): void => {
      if (shouldUseNativeCursor(event)) {
        useNativeCursor();
        return;
      }

      showCursor(event.clientX, event.clientY);
    },
    { passive: true },
  );

  document.addEventListener(
    "pointerdown",
    (event: PointerEvent): void => {
      if (event.button !== 0 || shouldIgnoreHeadpat(event)) {
        if (shouldUseNativeCursor(event)) {
          useNativeCursor();
        } else {
          showCursor(event.clientX, event.clientY);
        }
        return;
      }

      isPressing = true;
      document.documentElement.dataset.openvtHeadpatActive = "1";
      if (gifs.length > 0) {
        gifIndex = (gifIndex + 1) % gifs.length;
        setCursorSource(gifs[gifIndex], "patting");
      }
      showCursor(event.clientX, event.clientY);

      endHold();
      holdTimeout = window.setTimeout(
        () => void counter.increment(),
        config.headpat?.holdMilliseconds ?? 500,
      );
    },
    { passive: true },
  );

  const endPress = (): void => {
    if (!isPressing) {
      return;
    }

    isPressing = false;
    endHold();
    returnToIdleCursor();
  };

  document.addEventListener("pointerup", endPress, { passive: true });
  document.addEventListener("pointercancel", endPress, { passive: true });
  document.addEventListener(
    "pointerleave",
    (): void => {
      endPress();
      hideCursor();
      document.documentElement.classList.remove("headpat-native-cursor-zone");
    },
    { passive: true },
  );

  void counter.refresh();
}

function shouldIgnoreHeadpat(event: PointerEvent): boolean {
  return shouldUseNativeCursor(event);
}

function shouldUseNativeCursor(event: PointerEvent): boolean {
  return (
    event.composedPath().some((target) => isIgnoredPointerTarget(target)) ||
    isNearCreditsUi(event.clientX, event.clientY)
  );
}

function isIgnoredPointerTarget(target: EventTarget): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest(
      'a, button, input, select, textarea, [data-headpat-ignore="true"], [data-headpat-ignore]',
    ),
  );
}

function isNearCreditsUi(x: number, y: number): boolean {
  const creditsElements = document.querySelectorAll<HTMLElement>(
    "#credits, #credits .credits-more-list",
  );

  return Array.from(creditsElements).some((element) => {
    if (element.hidden || getComputedStyle(element).display === "none") {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return (
      x >= rect.left - nativeCursorPaddingPx &&
      x <= rect.right + nativeCursorPaddingPx &&
      y >= rect.top - nativeCursorPaddingPx &&
      y <= rect.bottom + nativeCursorPaddingPx
    );
  });
}

function createCounter(config: HeadpatWebConfig): {
  refresh: () => Promise<void>;
  increment: () => Promise<void>;
} {
  const counterLabel = document.getElementById("headpatsCounter");
  const counterValue = document.getElementById("counter");
  const counterConfig = config.headpat?.counter;
  const label = counterConfig?.label ?? "Headpats";
  const initialText = counterConfig?.initialText ?? "0";
  let localCount = Number.parseInt(initialText, 10);

  if (counterLabel) {
    counterLabel.firstChild?.replaceWith(`${label}: `);
  }
  if (counterValue) {
    counterValue.textContent = initialText;
  }
  document.documentElement.dataset.openvtHeadpatCount = initialText;

  const setCountFromText = (text: string): void => {
    const value = parseCounterValue(text, counterConfig?.responsePattern);
    if (!value) {
      return;
    }

    localCount = Number.parseInt(value, 10);
    if (counterValue) {
      counterValue.textContent = value;
    }
    document.documentElement.dataset.openvtHeadpatCount = value;
  };

  return {
    refresh: async (): Promise<void> => {
      if (!counterConfig?.getUrl) {
        return;
      }

      try {
        const response = await fetch(counterConfig.getUrl);
        setCountFromText(await response.text());
      } catch (error) {
        console.warn("Failed to fetch headpat counter.", error);
      }
    },
    increment: async (): Promise<void> => {
      if (!counterConfig?.incrementUrl) {
        localCount = Number.isFinite(localCount) ? localCount + 1 : 1;
        setCountFromText(String(localCount));
        return;
      }

      try {
        const response = await fetch(counterConfig.incrementUrl);
        setCountFromText(await response.text());
      } catch (error) {
        console.warn("Failed to increment headpat counter.", error);
      }
    },
  };
}

function parseCounterValue(text: string, pattern?: string): string | null {
  const match = pattern
    ? text.match(new RegExp(pattern))
    : text.match(/count:\s*(\d+)/i) ?? text.match(/\b(\d+)\b/);

  return match?.[1] ?? null;
}
